import { Alert, Box, Button, ButtonGroup, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, ListItemText, Menu, MenuItem, Select, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { IFactura } from "../Facturas";
import IPaciente from "../../../types/Paciente";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import IAlert from "../../../types/Alert";
import * as constantes from "../../../common/Constantes";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import DownloadIcon from '@mui/icons-material/GetApp';
import EmailIcon from '@mui/icons-material/Email';
import MoreIcon from '@mui/icons-material/MoreVert';
import ConfigUtils from "../../../utils/Config";
import moment from "moment";
import { IConcepto } from "../facturarCita/DetalleFactura";
import React from "react";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import BonoService from "../../../services/BonoService";
import DocUtils from "../../../utils/Doc";
import FacturacionService from "../../../services/FacturacionService";
import { IFacturaMetodoPago } from "../facturarCita/FacturarCitaDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { getEstadoFacturacion } from "../common/EstadoFacturacionIcon";
import PacientesUtils from "../../../utils/Pacientes";
import RectificarFacturaBono from "./RectificarFacturaBono";
import { formatCurrency, formatNumeroFactura } from "../../../utils/Factura";
import { getFacturaAcciones } from "../common/facturaAcciones";
import NumeroOrientativoEmision, { cargarNumeroOrientativo, InfoNumeroOrientativo } from "../common/NumeroOrientativoEmision";
import { chipEstadoFactura, chipEstadoPago } from "../common/chipsEstadoFactura";
import EnlaceFactura from "../common/EnlaceFactura";

interface DetalleFacturaBonoProps {
    open: boolean;
    onClose: () => void;
    factura?: IFacturaCompleta;
    paciente?: IPaciente;
    facturaId?: string;
    onFacturaPagada: (factura: IFactura) => void;
    onFacturaEmitida: (factura: IFactura) => void;
    onFacturaRectificada?: (factura: IFacturaCompleta) => void;
}

const DetalleFacturaBono = ({ open, onClose, factura, paciente, facturaId, onFacturaPagada, onFacturaEmitida, onFacturaRectificada}: DetalleFacturaBonoProps) => {
    const history = useNavigate();
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
    const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });
    const [loading, setLoading] = useState(false);
    const [showMissingAlert, setShowMissingAlert] = useState(true);
    const [modalPagar, setModalPagar] = useState(false);
    const [loadingPagar, setLoadingPagar] = useState(false);
    const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
    const [errorMetodoPago, setErrorMetodoPago] = useState<string | undefined>(undefined);
    const [errorFechaPago, setErrorFechaPago] = useState<string | undefined>(undefined);
    const [loadingEmitir, setLoadingEmitir] = useState(false);
    const [loadingEnviar, setLoadingEnviar] = useState(false);
    const [expandirDetalle, setExpandirDetalle] = useState(false);
    const [modalEmitir, setModalEmitir] = useState(false);
    const [numeroOrientativo, setNumeroOrientativo] = useState<InfoNumeroOrientativo | null>(null);
    const [erroresPaciente, setErroresPaciente] = useState<string[]>([]);
    const [localFactura, setLocalFactura] = useState(factura);
    const monedaFactura = localFactura?.moneda || 'EUR';
    const acciones = getFacturaAcciones(localFactura ?? {});
    const [localPaciente, setLocalPaciente] = useState(paciente);

    const [facturaRectificar, setFacturaRectificar] = useState<IFacturaCompleta | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const metodosPago = ConfigUtils.getMetodosPago();
    const userRole: number = ConfigUtils.getUserRole();

    useEffect(() => {
        if (!localFactura || !localPaciente) load();
    }, []);

    const load = async () => {
        try {
            setLoading(true);
            if (!facturaId) return;
            const response = await BonoService.getDetalleBono(facturaId);
            setLocalFactura(response.data.factura);
            setLocalPaciente(response.data.paciente);
        }  catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            const error: ILog = {
              method: "DetalleFacturaBono.Component.load",
              level: "error",
              message: e.message,
              meta: e.response,
            };
            LogService.logError(error);
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
          }
        } finally {
          setLoading(false);
        }
    };

    const openModalPagar = () => {
      if (localFactura?.estadoPago === "Pagada" && localFactura.metodoPago) {
        setMetodoPago({
          nombre: localFactura.metodoPago.nombre,
          codigo: localFactura.metodoPago.codigo,
          fechaPago: moment(localFactura.metodoPago.fechaPago),
        });
      }
      setModalPagar(true);
    };

    const openModalEmitir = async () => {
      const errores: string[] = [];

      if (!localFactura?.receptor?.nombre) errores.push("Nombre");
      if (!localFactura?.receptor?.nif) errores.push("DNI");

      if (errores.length > 0) {
        setErroresPaciente(errores);
      }
      setNumeroOrientativo(errores.length === 0 ? await cargarNumeroOrientativo(localFactura?._id) : null);
      setModalEmitir(true);
    };

    const closeModalPagar = () => {
      setMetodoPago(undefined);
      setErrorMetodoPago(undefined);
      setErrorFechaPago(undefined);
      setModalPagar(false);
    };

    const closeModalEmitir = () => {
      setErroresPaciente([]);
      setModalEmitir(false);
    };

    const pagarFactura = async () => {
      let hasError = false;

      if (!metodoPago) {
        setErrorMetodoPago("Debes seleccionar un método de pago");
        hasError = true;
      }
      if (!metodoPago?.fechaPago) {
        setErrorFechaPago("Debes seleccionar una fecha de pago");
        hasError = true;
      } else if (!moment.isMoment(metodoPago.fechaPago) || !metodoPago.fechaPago.isValid()) {
        setErrorFechaPago("Debes seleccionar una fecha de pago válida");
        hasError = true;
      }

      if (hasError) return;
      if (!metodoPago) return;

      setErrorMetodoPago(undefined);
      setErrorFechaPago(undefined);

      try {
        setLoadingPagar(true);
        const response = await FacturacionService.pagarFactura(localFactura?._id!, { 
          tipoPago: "bono",
          metodoPago });

        const facturaActualizada: IFactura = {
          _id: response.data.factura._id,
          numero: response.data.factura.numero,
          fechaFactura: response.data.factura.fechaFactura,
          tipo: response.data.factura.tipo,
          modalidad: response.data.tipoFacturacion,         
          servicio: response.data.factura.servicio,
          pacienteId: "",
          paciente: response.data.factura.receptor?.nombre ?? null,
          servicioId: "",
          total: response.data.factura.importeTotal,
          estado: getEstadoFacturacion(response.data.factura.estado, response.data.factura.estadoPago),
          facturaRelacionada: undefined,
          fechaPago: response.data.factura.metodoPago?.fechaPago?.toString() ?? null
        };

        setLocalFactura(prev => ({
          ...prev!,
          estadoPago: "Pagada",
          metodoPago: metodoPago,
        }));

        onFacturaPagada(facturaActualizada);
        closeModalPagar();

      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "DetalleFacturaBono.Component.pagarFactura",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingPagar(false);
      }
    };

    const emitirFactura = async () => {
      try {
        setLoadingEmitir(true);
        const response = await FacturacionService.emitirFactura(localFactura?._id!, { 
          userId: ConfigUtils.getUserId(),
          tipoPago: "bono" });
        
        const facturaActualizada: IFactura = {
          _id: response.data.factura._id,
          numero: response.data.factura.numero,
          fechaFactura: response.data.factura.fechaFactura,
          tipo: response.data.factura.tipo,
          modalidad: response.data.tipoFacturacion,         
          servicio: response.data.factura.servicio,
          pacienteId: "",
          paciente: response.data.factura.receptor?.nombre ?? null,
          servicioId: "",
          total: response.data.factura.importeTotal,
          estado: getEstadoFacturacion(response.data.factura.estado, response.data.factura.estadoPago),
          facturaRelacionada: undefined
        };

        setLocalFactura(prev => ({
          ...prev!,
          estado: "Emitida",
          numero: response.data.factura.numero,
          fechaEmision: response.data.factura.fechaEmision,
        }));

        onFacturaEmitida(facturaActualizada);
        closeModalEmitir();

      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "DetalleFacturaBono.Component.emitirFactura",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingEmitir(false);
      }
    };

    const handleDownloadFactura = () => {
      DocUtils.formatFactura(localFactura, true, "total");
    };

    const handleEnviarFactura = async () => {
      try {
        setLoadingEnviar(true);
        const facturaPDF = DocUtils.formatFactura(localFactura, false, "total");
        const response = await FacturacionService.sendFactura({ facturaId: localFactura?._id, email: localPaciente?.email || paciente?.email, doc: facturaPDF, nombre: localFactura?.receptor.nombre, numeroFactura: localFactura?.numero });
        setAlert({ open: true, severity: 'success', msg: response.data.message });
      } catch (e: any) {
        if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
        } else {
            const error: ILog = { method: 'DetalleFacturaBono.Component.sendFactura', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        }
      } finally {
        setLoadingEnviar(false);
      }
    };

    const missingFields = localPaciente
    ? [
        !localPaciente.domicilio ? "Dirección" : null,
        !localPaciente.DNI ? "NIF" : null,
      ].filter((f): f is string => Boolean(f))
    : [];

    const { isDisabledEmail, tooltipMsg } = PacientesUtils.canSendFactura(
      loadingEnviar,
      localPaciente?.authEmail || paciente?.authEmail,
      localPaciente?.email || paciente?.email,
      localFactura?.estado,
      localFactura?.facturaRectificadaId
    );

    return (
        <>
            <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
                {loading ? <CircularProgress style={{ margin: "20px auto" }} /> : <>
                <Box sx={{ p: 2, backgroundColor: "#f8f9fa", border: "1px solid #e5e5e5" }}>
                    {/* Aviso de datos incompletos */}
                    {missingFields.length > 0 && showMissingAlert && localFactura && localFactura.estado !== "Emitida" && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 1, fontSize: "0.875rem" }}
                      onClose={() => setShowMissingAlert(false)}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">
                          Faltan datos importantes: <b>{missingFields.join(", ")}</b>.
                        </Typography>
                    
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => history(constantes.R_NUEVOPACIENTE, { state: paciente })}
                        >
                          Editar
                        </Button>
                      </Box>
                    </Alert>
                    )}

                    {/* Emisor — solo si hay más de una entidad de facturación configurada */}
                    {localFactura?.emisor && ConfigUtils.getDatosFacturacionAdicional().length > 0 && (
                      <Box display="flex" alignItems="center" gap={0.5} mb={1.5} pb={1} sx={{ borderBottom: "1px solid #e5e5e5" }}>
                        <Typography variant="body2" color="text.secondary">
                          <b>Entidad emisora:</b> {localFactura.emisor.razonSocial}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between">
                      {/* Datos del receptor */}
                      <Box flex={1}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Receptor
                        </Typography>
                        <Typography variant="body2">
                          <b>Nombre:</b> {localFactura?.receptor.nombre}
                        </Typography>
                        {localFactura?.receptor.nif &&
                        <Typography variant="body2">
                          <b>NIF:</b> {localFactura?.receptor.nif}
                        </Typography>}
                        {localFactura?.receptor.direccion &&
                        <Typography variant="body2">
                          <b>Domicilio:</b> {localFactura?.receptor.direccion}
                        </Typography>}
                        {localFactura?.receptor.email &&
                        <Typography variant="body2">
                          <b>Email:</b> {localFactura?.receptor.email}
                        </Typography>}
                      </Box>
                    </Box>
                </Box>

                <DialogContent>
                  {/* INFO GENERAL DE LA FACTURA */}
                  <Box mb={isMobile ? 2 : 3}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600 }}
                      >
                        Información de la factura
                      </Typography>       
                      {/* Acciones */}
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Descargar factura">
                          <IconButton
                            size="small"
                            onClick={handleDownloadFactura}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={localFactura?.estado === 'Anulada' ? 'Factura anulada' : tooltipMsg}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleEnviarFactura}
                              disabled={isDisabledEmail || localFactura?.estado === 'Anulada'}
                            >
                              {loadingEnviar ? (
                                <CircularProgress size={18} />
                              ) : (
                                <EmailIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>        
                        
                    <Box mb={1}>
                      {/* --- Fila principal: Nº, Fecha, Estado --- */}
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        justifyContent="flex-start"
                        alignItems={isMobile ? "flex-start" : "center"}
                        spacing={isMobile ? 1 : 2}
                      >
                        {/* Nº */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Nº
                          </Typography>
                          <Typography variant="body2">{formatNumeroFactura(localFactura?.numero)}</Typography>
                        </Stack>

                        {/* Fecha */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Fecha
                          </Typography>
                          <Typography variant="body2">{new Date(localFactura?.fechaFactura!).toLocaleDateString()}</Typography>
                        </Stack>

                        {/* Estado */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 140 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Estado
                          </Typography>
                          {localFactura?.estado === "Anulada" ? (
                            <Tooltip
                              title={localFactura?.motivoAnulacion}
                              placement="top"
                              arrow
                            >
                              <Chip
                                label={localFactura.estado}
                                size="small"
                                sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                              />
                            </Tooltip>  
                          ) : (
                            <Chip
                              {...chipEstadoFactura(localFactura)}
                              size="small"
                              sx={{ textTransform: "capitalize" }}
                            />  
                          )}
                        </Stack>
                      </Stack>

                      {/* --- Fila final: Información de rectificación --- */}
                      {(localFactura?.facturaOriginalId || localFactura?.facturaRectificadaId) && (
                        <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                          {localFactura?.facturaOriginalId && (
                            <Stack direction="row" spacing={1}>
                              <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                                {localFactura?.tipoRectificacion === "anulacion" ? "Anulación de la factura nº" : "Rectificativa de la factura nº"}
                              </Typography>
                              <EnlaceFactura facturaId={localFactura?.facturaOriginalRef} numero={localFactura?.facturaOriginalId} cursiva />
                            </Stack>
                          )}
                          {localFactura?.facturaRectificadaId && (
                            <Stack direction="row" spacing={1}>
                              <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                                {localFactura?.estado === "Anulada" ? "Anulada por la factura nº" : "Rectificada por la factura nº"}
                              </Typography>
                              <EnlaceFactura facturaId={localFactura?.facturaRectificadaId} numero={localFactura?.facturaRectificada} cursiva />
                            </Stack>
                          )}
                        </Stack>
                      )}
                    </Box>

                  </Box>
                  <Divider sx={{ mb: 2 }} />                   
                  <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    justifyContent="space-between"
                    alignItems={isMobile ? "stretch" : "center"}
                    mb={2}
                    gap={isMobile ? 1.5 : 4}
                  >
                    {/* INFO IZQUIERDA */}
                    <Box display="flex" flexDirection="column" gap={isMobile ? 0.75 : 1}>
                      {/* Estado de pago */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Estado
                        </Typography>
                        <Chip
                          {...chipEstadoPago(localFactura)}
                          size="small"
                        />
                      </Box>
                  
                      {/* Tipo de facturación */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Tipo
                        </Typography>
                        <Chip
                          label={"Total"}
                          size="small"
                          color="info"
                        />
                      </Box>
                  
                      {/* Servicio */}
                      <Box display="flex" justifyContent="space-between" gap={{ xs: 0, sm: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Servicio
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {localFactura?.servicio}
                        </Typography>
                      </Box>
                    </Box>
                  
                    {/* IMPORTE */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems={isMobile ? "flex-start" : "flex-end"}
                      mt={isMobile ? 1 : 0}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Importe
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(Number(localFactura?.importeTotal), monedaFactura)}
                      </Typography>
                    </Box>
                  </Box>
                  {/* Si la factura está pagada, método de pago */}
                  {localFactura?.estadoPago === "Pagada" && (
                  <Box
                    mb={3}
                    p={2}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      backgroundColor: "background.paper",
                      maxWidth: 400,
                    }}
                  >
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="body2">
                        <b>Método de pago:</b> {localFactura?.metodoPago?.nombre}
                      </Typography>
                      <Typography variant="body2">
                        <b>Fecha de pago:</b> {moment(localFactura?.metodoPago?.fechaPago).format('DD/MM/YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                  )}              
                  <Divider />
                  <Box mt={2} display="flex" justifyContent="space-between">
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={() => setExpandirDetalle(!expandirDetalle)}
                    >
                      {expandirDetalle ? "Ocultar detalles" : "Ver detalles"}
                    </Button>

                    <Box display="flex" gap={1} alignItems="center">

                    {acciones.mostrarBotonesAccion && (
                      <ButtonGroup variant="outlined" color="success">
                        {acciones.mostrarPagar && (
                          <Button variant={acciones.variantPagar} onClick={openModalPagar}>Pagar</Button>
                        )}
                        {acciones.mostrarEmitir && (
                          <Button variant={acciones.variantEmitir} onClick={openModalEmitir}>Emitir</Button>
                        )}
                      </ButtonGroup>
                    )}
                    {userRole === constantes.ROLES.ADMINISTRADOR &&
                      acciones.mostrarMenuAcciones && (
                        <>
                          <Tooltip title="Más acciones">
                            <IconButton
                              size="small"
                              onClick={(e) => setAnchorEl(e.currentTarget)}
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                          >
                            {acciones.mostrarCorregirCobro && <MenuItem
                              onClick={() => {
                                setAnchorEl(null);
                                openModalPagar();
                              }}
                            >
                              <ListItemText>
                                Corregir cobro
                              </ListItemText>
                            </MenuItem>}
                            {localFactura && acciones.mostrarRectificar && <MenuItem
                              onClick={() => {
                                setAnchorEl(null);
                                setFacturaRectificar(localFactura);
                              }}
                            >
                              <ListItemText>
                                Rectificar factura
                              </ListItemText>
                            </MenuItem>}

                          </Menu>
                        </>
                    )}
                  </Box>
                  </Box>
                  {/* Bloque expandible */}
                  <Collapse in={expandirDetalle} timeout="auto" unmountOnExit>
                    <Box
                      mt={2}
                      p={2}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: "background.paper",
                        overflowX: "auto",
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        Detalle de conceptos
                      </Typography>
                  
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Concepto</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Precio unit.</TableCell>
                            <TableCell align="right">Subtotal</TableCell>
                          </TableRow>
                        </TableHead>
                  
                        <TableBody>
                          {localFactura?.conceptos.map((c: IConcepto, index: number) => {
                            const imp = localFactura?.impuestos?.[index];
                            const base = c.cantidad * c.precio;
                            const cuota = imp ? base * (imp.cuota / 100) : 0;
                            const total = base + cuota;
                        
                            return (
                              <React.Fragment key={index}>
                                {/* LÍNEA PRINCIPAL */}
                                <TableRow hover>
                                  <TableCell>{c.nombre}</TableCell>
                                  <TableCell align="right">{c.cantidad}</TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(c.precio, monedaFactura)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(base, monedaFactura)}
                                  </TableCell>
                                </TableRow>
                            
                                {/* LÍNEA DETALLE: IMPUESTO + TOTAL */}
                                {imp && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={4}
                                      sx={{
                                        pl: 6,
                                        py: 0.8,
                                        backgroundColor: "action.hover",
                                        borderBottom: "none",
                                      }}
                                    >
                                      <Typography variant="body2" color="text.secondary">
                                        {imp.tipo} ({imp.cuota}%): {formatCurrency(cuota, monedaFactura)}
                                        &nbsp;•&nbsp;
                                        <strong>Total: {formatCurrency(total, monedaFactura)}</strong>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                      
                </DialogContent>
                <DialogActions>
                  <Button onClick={onClose}>
                    Cerrar
                  </Button>
                </DialogActions>
                </>}
            </Dialog>

            {/* Modal pagar */}
            {modalPagar && (
            <Dialog open={modalPagar} onClose={loadingPagar ? undefined : closeModalPagar} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>
                {localFactura?.estadoPago === "Pagada" ? "Corregir cobro" : "Pagar factura"}
              </DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                {/* Importe total */}
                <Box
                  sx={{
                    mb: 3,
                    mt: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "grey.100",
                    textAlign: "center"
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "success.main" }}>
                    {formatCurrency(Number(localFactura?.importeTotal), monedaFactura)}
                  </Typography>
                </Box>
                
                {/* Método de pago */}
                <FormControl fullWidth error={!!errorMetodoPago}>
                  <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                  <Select
                    labelId="metodo-pago-label"
                    label="Método de pago*"
                    value={metodoPago?.nombre || ""}
                    onChange={(e) => {
                      const nombre = e.target.value;
                      const codigo = metodosPago.find((m) => m.nombre === nombre)?.codigo!;
                      setMetodoPago({ nombre, codigo, fechaPago: moment() });
                      setErrorMetodoPago(undefined);
                    }}
                  >
                    {metodosPago
                      .filter((m) => m.activo)
                      .map((m) => (
                        <MenuItem key={m.codigo} value={m.nombre}>
                          {m.nombre}
                        </MenuItem>
                      ))}
                  </Select>
                    
                  {errorMetodoPago && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                      {errorMetodoPago}
                    </Typography>
                  )}
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Fecha de pago"
                    value={metodoPago?.fechaPago}
                    onChange={(date) => {
                      if (metodoPago) {
                        setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                        setErrorFechaPago(undefined);
                      }
                    }}
                    renderInput={(params) => <TextField required fullWidth sx={{ mt: 2 }} error={!!errorFechaPago} helperText={errorFechaPago} {...params} />}
                  />
                </LocalizationProvider>
              </DialogContent>              
              <DialogActions sx={{ p: 2 }}>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={pagarFactura}
                  disabled={loadingPagar}
                  startIcon={loadingPagar && <CircularProgress size={18} />}
                >
                  Pagar
                </Button>
                  
                <Button 
                  variant="outlined" 
                  color="inherit"
                  onClick={closeModalPagar}
                  disabled={loadingPagar}
                >
                  Cancelar
                </Button>
              </DialogActions>
            </Dialog>
            )}

            {/* Modal emitir */}
            {modalEmitir && 
            <Dialog open={modalEmitir} onClose={loadingEmitir ? undefined : closeModalEmitir} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>Emitir factura</DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                {erroresPaciente.length > 0 ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    No se puede emitir la factura. Faltan datos importantes:
                    <ul>
                      {erroresPaciente.map((e) => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : 
                <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {!localFactura?.receptor?.direccion && (
                    <>La factura se emitirá sin dirección del paciente.<br /><br /></>
                  )}
                  Una vez emitida, la factura no se podrá modificar.
                  Solo se podrá generar una factura rectificativa.
                </Alert>
    
                <NumeroOrientativoEmision info={numeroOrientativo} />

    
                <Typography>
                  ¿Deseas emitir esta factura definitivamente?
                </Typography>
                </>
                }
    
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                {erroresPaciente.length === 0 ? (
                  <>
                    <Button 
                      variant="contained" 
                      color="success"
                      onClick={emitirFactura}
                      disabled={loadingEmitir}
                      startIcon={loadingEmitir && <CircularProgress size={18} />}
                    >
                      Emitir
                    </Button>
                
                    <Button 
                      variant="outlined" 
                      color="inherit"
                      onClick={closeModalEmitir}
                      disabled={loadingEmitir}
                    >
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="outlined" 
                    color="inherit"
                    onClick={closeModalEmitir}
                  >
                    Cerrar
                  </Button>
                )}
              </DialogActions>
            </Dialog>}

            {facturaRectificar !== null &&
            <RectificarFacturaBono 
              factura={facturaRectificar}
              pacienteId={localPaciente?._id!}
              open={!!facturaRectificar}
              onClose={() => setFacturaRectificar(null)}
              onFacturaRectificada={(facturaRectificativa: IFacturaCompleta) => {
                setLocalFactura(prev => {
                  if (!prev) return prev;
                
                  return {
                    ...prev,
                    facturaRectificada: facturaRectificativa.numero,
                    facturaRectificadaId: facturaRectificativa._id
                  };
                });
                if (onFacturaRectificada) onFacturaRectificada(facturaRectificativa);
              }}
            />
            }

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
              <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
                {alert.msg}
              </Alert>
            </Snackbar>
        </>
    );
};

export default DetalleFacturaBono;